interface AppliedBinding { sourceId: number; boundAtMs: number }
interface Dependencies {
  activate(camera: string): void;
  binding(camera: string): AppliedBinding | undefined;
  refreshSources(): void;
  subscribe(listener: () => void): () => void;
  now(): number;
}

/** Activate the native producer even if Chromium already holds its placeholder track. */
export class RecorderCameraPreparation {
  private readonly pending = new Map<string, Promise<void>>();
  public constructor(private readonly dependencies: Dependencies, private readonly timeoutMs = 5_000) {}

  public prepare(camera: string): Promise<void> {
    this.dependencies.activate(camera);
    const pending = this.pending.get(camera);
    if (pending) return pending;
    const binding = this.dependencies.binding(camera);
    // Preserve an applied window/display binding; enumeration can rebind other cameras.
    if (binding && binding.sourceId >= 0) return Promise.resolve();
    const operation = this.bind(camera).finally(() => this.pending.delete(camera));
    this.pending.set(camera, operation);
    return operation;
  }

  private bind(camera: string): Promise<void> {
    const started = this.dependencies.now();
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      let unsubscribe: () => void = () => undefined;
      const finish = (error?: Error) => {
        if (settled) return;
        settled = true; clearTimeout(timer); unsubscribe();
        if (error) reject(error); else resolve();
      };
      const check = () => {
        const binding = this.dependencies.binding(camera);
        if (binding && binding.sourceId >= 0 && binding.boundAtMs >= started) finish();
      };
      const timer = setTimeout(() => finish(new Error('recorder_camera_source_unavailable')), this.timeoutMs);
      try {
        unsubscribe = this.dependencies.subscribe(check);
        if (settled) { unsubscribe(); return; }
        this.dependencies.refreshSources();
        check();
      } catch (error) { finish(error instanceof Error ? error : new Error('recorder_camera_source_unavailable')); }
    });
  }
}
